import { useEffect, useState } from 'react'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { CaptionSelection } from './CaptionSelection'
import { CaptionGenerationForm } from './CaptionGenerationForm'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useCompleteDesignAction } from '../utils/useCompleteDesignAction'
import { DesignActionType } from 'types'
import { useLastUsedLanguage } from 'hooks/queries/useLastUsedLanguage'

const CaptionTab = observer(() => {
  const { designStore, drawersStore } = useRootStore()
  const captionsStore = designStore?.captionsStore
  const [tones, setSelectedTones] = useState<Tone[]>([])
  const [voice, setSelectedVoice] = useState<number | undefined>()
  const [language, setLanguage] = useState<string>('')
  const [customPrompt, setCustomPrompt] = useState<string>('')
  const currentPanel = captionsStore?.panel ?? 'generate'
  const completeActionMutation = useCompleteDesignAction()
  const defaultLanguageQuery = useLastUsedLanguage()
  const defaultLanguage = defaultLanguageQuery?.data?.language

  useEffect(() => {
    if (!!defaultLanguage) setLanguage(defaultLanguage)
  }, [defaultLanguage, setLanguage])

  useEffect(() => {
    captionsStore?.openPanel('generate')
  }, [captionsStore])

  if (!captionsStore) {
    return <></>
  }

  const selectVoice = (voice?: number) => {
    if (tones.length) setSelectedTones([])
    setSelectedVoice(voice)
  }

  const selectTone = (tone: Tone) => {
    if (voice) setSelectedVoice(undefined)
    setSelectedTones((tones) => {
      if (tones.includes(tone)) return tones.filter((t) => t !== tone)
      return [...tones, tone]
    })
  }

  const handleGenerate = () => {
    captionsStore.generateCaptions(customPrompt, voice, tones, language)
    captionsStore.openPanel('select')
    completeActionMutation.mutate({ action: DesignActionType.GenerateCaption })
  }

  const handleGenerateMore = async () => {
    await captionsStore.generateCaptions(customPrompt, voice, tones, language)
  }

  const insertCaption = async (caption: string) => {
    await captionsStore.saveCaptions([caption], 0)
    designStore.openDefaultTab()
    captionsStore.openPanel('generate')
    drawersStore.closeDrawer()
  }

  const removeCaption = async () => {
    await captionsStore.deleteCaptions()
  }

  return currentPanel === 'select' ? (
    <CaptionSelection insertCaption={insertCaption} handleGenerateMore={handleGenerateMore} />
  ) : (
    <CaptionGenerationForm
      captionsStore={captionsStore}
      customPrompt={customPrompt}
      setCustomPrompt={setCustomPrompt}
      voice={voice}
      tones={tones}
      language={language}
      selectVoice={selectVoice}
      selectTone={selectTone}
      setLanguage={setLanguage}
      generate={handleGenerate}
      insertCaption={insertCaption}
      removeCaption={removeCaption}
    />
  )
})

export { CaptionTab }
